<template>
    <app-card class="mb-4">
        <div class="row">
            <div class="col mb-3">
                <input class="form-control custom-green-input-bg" :placeholder="`${$t('Search')}...`" type="text" v-model="term"/>
            </div>
            <div class="col-md-1">
                <app-button v-if="term" native-type="button" type="warning" @click="cancelFilter" class="mt-1" small>
                    {{ $t('Cancel') }}
                </app-button>
                <app-loader-gif class="mt-2" v-if="loading"/>
            </div>
            <div class="col-3 pt-2">
            </div>
            <div class="col-3">
                <app-datepicker id="past_search_dates"
                                v-model="past_search_dates"
                                range
                                :placeholder="$t('Past contracts')"
                                name="past_search_dates"
                                @input="pastContracts"/>
            </div>
        </div>
        <div class="table-responsive">
            <table class="table table-striped table-sm">
                <thead>
                    <tr>
                        <th @click="orderBy('num')" class="pointer">
                            #
                            <span v-html="order_field === 'num' ? active_sort_icon : sort_icon"/>
                        </th>
                        <th @click="orderBy('value.rentee.name')" class="pointer">
                            {{ $t('Name') }}
                            <span v-html="order_field === 'value.rentee.name' ? active_sort_icon : sort_icon"/>
                        </th>
                        <th @click="orderBy('value.rentee.phone')" class="pointer">
                            {{ $t('Phone') }}
                            <span v-html="order_field === 'value.rentee.phone' ? active_sort_icon : sort_icon"/>
                        </th>
                        <th>{{ $t('Pick up & return') }}</th>
                        <th>{{ $t('Vehicle') }}</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(contract, i) in orderedContracts" :key="i">
                        <td>
                            {{ contract.num }}
                            <template v-if="contract.booking_id === null">
                                <br>
                                <span class="mdi mdi-18px mdi-alert-outline text-danger" :title="$t('No booking for contract')"/>
                            </template>
                        </td>
                        <td>
                            {{ contract.value.rentee.name }} <br>
                            <small>{{ contract.value.rentee.email }}</small>
                        </td>
                        <td>{{ contract.value.rentee.phone }}</td>
                        <td>
                            {{ contract.booking.start_time | formatDateTime }} - {{ contract.booking.end_time | formatDateTime }}
                            <br>
                            {{ contract.booking.pickup_location.name[locale] }} - {{ contract.booking.return_location.name[locale] }}
                            <template v-if="contract.booking.travel_out">
                                <br>
                                <span class="mdi mdi-18px mdi-alert-outline text-danger"/> {{ $t('Travel out of Estonia') }}: <b>{{ contract.booking.travel_out }}</b>
                            </template>
                        </td>
                        <td>
                            {{ contract.booking.vehicle.make.name }} {{ contract.booking.vehicle.model.name }} <app-vehicle-no v-if="contract.booking.vehicle.slug" :slug="contract.booking.vehicle.slug"/>, {{ contract.booking.vehicle.reg_no }}
                        </td>
                        <td class="p-0 d-flex justify-content-end align-items-center">
                            <app-button v-if="$store.getters.canViewContracts" :loading="pdf_loading" native-type="button" type="info" class="text-white" @click="pdf(contract.id, contract.num, 'et')" small>
                                <fa-icon :icon="['fa', 'file-pdf']"/> EST
                            </app-button>
                            <app-button v-if="$store.getters.canViewContracts" :loading="pdf_loading" native-type="button" type="warning" class="text-white" @click="pdf(contract.id, contract.num, 'en')" small>
                                <fa-icon :icon="['fa', 'file-pdf']"/> ENG
                            </app-button>
                            <router-link v-if="$store.getters.canViewContracts" :to="{name: 'contracts.show', params: {contract_id: contract.id}}" class="btn btn-info btn-sm text-white">
                                {{ $t('View') }}
                            </router-link>
                            <app-button v-if="$store.getters.canDeleteContracts" native-type="button" type="danger" @click="destroy(contract.id)" small>
                                <fa-icon :icon="['fa', 'trash-alt']"/>
                            </app-button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </app-card>
</template>

<script>
import ContractService from '@services/contract.service';
import {mapGetters} from 'vuex';
import orderBy from 'lodash/orderBy';
import pdfMixin from './pdf.mixin';

export default {
    mixins: [pdfMixin],
    data() {
        return {
            loading: true,
            term: '',
            contact_type: 'current',
            past_search_dates: [],
            order_field: 'created_at',
            order_dir: 'desc',
            sort_icon: '<span class="mdi mdi-18px mdi-menu-swap"/>',
            contracts: []
        };
    },
    computed: {
        ...mapGetters(['locale']),
        filteredContracts() {
            if (this.contracts.length) {
                return this.contracts.filter((contract,i) => {
                    const term = this.term.toString().toLowerCase();
                    const booking = contract.booking;

                    return (
                        contract.value.rentee.name.toString().toLowerCase().match(term) ||
                        contract.value.rentee.phone.toString().toLowerCase().match(term) ||
                        contract.value.rentee.email.toString().toLowerCase().match(term) ||
                        booking.vehicle && booking.vehicle.reg_no && booking.vehicle.reg_no.toLowerCase().match(term)
                    );
                });
            }
            return false;
        },
        orderedContracts() {
            return orderBy(this.filteredContracts, this.order_field, this.order_dir);
        },
        active_sort_icon() {
            if (this.order_dir === 'desc') {
                return '<span class="mdi mdi-18px mdi-menu-down"/>';
            } else {
                return '<span class="mdi mdi-18px mdi-menu-up"/>';
            }
        }
    },
    methods: {
        orderBy(field) {
            this.order_field = field;
            if (this.order_dir === 'desc') {
                this.order_dir = 'asc';
            } else {
                this.order_dir = 'desc';
            }
        },
        cancelFilter() {
            this.term = '';
        },
        getContracts(type) {
            this.loading = true;

            if(type === 'current') {
                ContractService.currentContracts().then(res => {
                    this.contracts = res;
                    this.loading = false;
                });
                return;
            }
        },
        pastContracts(dates) {
            if (dates[0] == null || dates[1] == null) {
                this.contact_type = 'current';
                this.getContracts('current');
                return;
            }
            this.contact_type = false;
            this.loading = true;
            this.contracts = [];
            ContractService.pastContracts(dates).then(res => {
                this.contracts = res;
                this.loading = false;
            });
        },
        destroy(contractId) {
            if(!window.confirm(`${this.$t('Delete')}?`)) {
                return false;
            }
            ContractService.destroy(contractId).then(res => {
                const index = this.contracts.findIndex(c => c.id === contractId);
                this.contracts.splice(index, 1);
                this.$toastr.s(this.$t('Deleted'));
            });
        }
    },
    created() {
        this.getContracts('current');
    }
};
</script>
